// Must stay the first import: puts the login token on every API fetch (TP1, 2026-09-27).
import './apiAuthInstall';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import ErrorBoundary from './ErrorBoundary';
// M-H6 (KALI mobile audit 2026-10-04): fonts are self-hosted (were 3 Google families, a
// render-blocking third-party stylesheet on every cold LTE start). Same family names, so no
// font-family string anywhere changes; unicode-range subsets download on demand.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import '@fontsource/inter/900.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import '@fontsource/jetbrains-mono/700.css';
// @ts-ignore
import './index.css';

// ── AUTO-UPDATE ──────────────────────────────────────────────────────────────
// Phone par SPA tab kabhi reload nahi hota → naya deploy user ko KABHI nahi
// dikhta (2026-06-12: poori subah "purana hi aa raha hai" isi se thi). Tab
// wapas visible hone par (+ har 5 min) live index.html ka bundle-hash check;
// naya build mile to khud reload. Active use ke beech kabhi reload nahi karta.
const _curBundle = (document.querySelector('script[type="module"][src*="/assets/index-"]') as HTMLScriptElement | null)
  ?.src.match(/index-[\w-]+\.js/)?.[0];
let _updBusy = false;
async function _checkForNewBuild() {
  if (_updBusy || !_curBundle || document.visibilityState !== 'visible') return;
  _updBusy = true;
  try {
    const res  = await fetch('/index.html?upd=' + Date.now(), { cache: 'no-store' });
    const html = await res.text();
    const latest = html.match(/index-[\w-]+\.js/)?.[0];
    if (latest && latest !== _curBundle) {
      // NEVER RELOAD OUT FROM UNDER A TRADER (God 2026-09-15, iOS audit).
      // On an iPhone the app is backgrounded and re-opened constantly, and
      // this check runs on every return. A reload here would tear down an
      // ARMED hold-to-fire bar, a half-filled deploy modal, or an open bottom
      // sheet - and the comment above promised "active use ke beech kabhi
      // reload nahi karta" while the code below did exactly that. The new
      // build is ANNOUNCED instead; the shell shows an "update ready" pill and
      // the reload happens on the user's tap, or silently only when nothing
      // is armed and nothing is open.
      (window as any).__jcUpdateReady = latest;
      window.dispatchEvent(new CustomEvent('jc-update-ready', { detail: { bundle: latest } }));
    }
  } catch { /* offline / transient — ignore */ }
  finally { _updBusy = false; }
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') _checkForNewBuild();
});
setInterval(_checkForNewBuild, 5 * 60 * 1000);

// PWA MOBILE CONSOLE (God 2026-08-30) → DIRECT OPEN (God 2026-09-05).
// /m used to mount a read-only summary whose only action was an "OPEN TRADING
// CONSOLE" link to /?page=LiveAutoTrading. God asked for that intermediate hop
// to go, so /m now hands straight off to the console it used to link to.
//
// The redirect runs BEFORE React mounts, so the summary never paints — a
// redirect inside the component would flash the old screen first. `replace`
// keeps the back button sane. Same origin, and the manifest scope is "/", so an
// installed PWA stays inside the app instead of kicking out to the browser.
//
// This is NAVIGATION ONLY. No order path is added here: LiveTrading remains the
// single place an order can be fired, which is the whole point of b7ffe8d, and
// the console's own jc_auth_session still gates it. MobileApp.tsx is left
// untouched on disk — to restore the summary, re-import it and render it here.
const _isMobileApp = window.location.pathname === '/m'
  || window.location.pathname.startsWith('/m/');

// KALI Phase 3 P3-H6 (God D22, 2026-10-04): a browser crash is WRITTEN DOWN (POST /api/ui-crash -> agent_notebooks/
// ui_crash.jsonl). Until now no UI error ever reached the server, so "the chart does not crash" could be neither
// proven nor refuted. At most 5 reports per page load; never throws, never blocks.
let _uiCrashSent = 0;
const _reportUiCrash = (kind: string, message: string, stack?: string) => {
  try {
    if (_uiCrashSent >= 5) return;
    _uiCrashSent += 1;
    const mem = (performance as any)?.memory ? Math.round((performance as any).memory.usedJSHeapSize / 1048576) + 'MB' : '';
    // the static site has no API: post to the api host, on a prefix nginx sends to FastAPI (/system/)
    const _api = (import.meta as any).env?.VITE_API_URL || 'https://api.jaiswalcapital.com';
    fetch(`${_api}/system/ui-crash`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
      body: JSON.stringify({ kind, message: String(message || '').slice(0, 1000), stack: String(stack || '').slice(0, 2000),
        url: window.location.pathname + window.location.search, ua: navigator.userAgent.slice(0, 200), mem, ..._uiEnv() }) }).catch(() => {});
  } catch { /* reporting must never crash the page */ }
};
// M-H5 (KALI mobile audit, 2026-10-04): the device context every report carries. iOS Safari
// never "refreshes" a page on its own: a tab that comes back reloaded was killed for memory
// (jetsam). These fields say what kind of device / mode / visibility it happened in.
function _navType(): string {
  try { return String(((performance.getEntriesByType('navigation')[0] as any) || {}).type || ''); } catch { return ''; }
}
function _uiEnv(): Record<string, string> {
  const env: Record<string, string> = {};
  try {
    env.deviceMemory = String((navigator as any).deviceMemory ?? '');
    env.standalone = String(!!((navigator as any).standalone || window.matchMedia?.('(display-mode: standalone)').matches));
    env.visibility = document.visibilityState;
    env.navType = _navType();
    env.dpr = String(window.devicePixelRatio || 1);
    env.vw = `${window.innerWidth}x${window.innerHeight}`;
  } catch { /* best effort */ }
  return env;
}

// M-H5 jetsam-reload detector. Every 5 s (only while visible) the tab stamps a heartbeat in
// sessionStorage, which survives a reload of the same tab. On load: a heartbeat younger than
// 30 s + navigation.type === 'reload' + no user-initiated reload flag = the page was torn
// down and rebuilt under us. One line, kind 'jetsam_reload'. The detail is ALSO written into
// `message`, so a server that only whitelists the original fields still records it.
const _HB_KEY = 'jc_hb', _USER_RELOAD_KEY = 'jc_user_reload';
try {
  const prev = Number(sessionStorage.getItem(_HB_KEY) || 0);
  const userReload = sessionStorage.getItem(_USER_RELOAD_KEY) === '1';
  sessionStorage.removeItem(_USER_RELOAD_KEY);
  const age = prev ? (Date.now() - prev) / 1000 : -1;
  if (prev && age >= 0 && age < 30 && _navType() === 'reload' && !userReload) {
    const e = _uiEnv();
    _reportUiCrash('jetsam_reload',
      `jetsam_reload hb_age=${age.toFixed(1)}s nav=${e.navType} dm=${e.deviceMemory} standalone=${e.standalone} dpr=${e.dpr} vw=${e.vw}`);
  }
} catch { /* storage blocked: no detector, nothing breaks */ }
const _beat = () => { try { if (document.visibilityState === 'visible') sessionStorage.setItem(_HB_KEY, String(Date.now())); } catch { /* blocked */ } };
_beat();
setInterval(_beat, 5000);
// A reload the USER asked for (update pill, pull-to-refresh via keyboard) is not a crash.
window.addEventListener('keydown', (e) => {
  if (e.key === 'F5' || ((e.ctrlKey || e.metaKey) && (e.key === 'r' || e.key === 'R'))) {
    try { sessionStorage.setItem(_USER_RELOAD_KEY, '1'); } catch { /* blocked */ }
  }
});
(window as any).__jcMarkUserReload = () => { try { sessionStorage.setItem(_USER_RELOAD_KEY, '1'); } catch { /* blocked */ } };
window.addEventListener('error', (e) => _reportUiCrash('error', e.message, (e.error && e.error.stack) || ''));
window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) =>
  _reportUiCrash('unhandledrejection', String((e.reason && e.reason.message) || e.reason || ''), (e.reason && e.reason.stack) || ''));
(window as any).__jcReportUiCrash = _reportUiCrash;

if (_isMobileApp) {
  window.location.replace('/?page=LiveAutoTrading');
} else {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>,
  );
}